import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Package, Gift, Droplets, Truck, Award, Wine, Sparkles, Smartphone, CreditCard, Lock, MessageSquare, AlertTriangle, Mail } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../context/AuthContext'

interface StoreSettings {
  pickup_enabled: string
  pickup_address: string
  pickup_hours: string
  pickup_notes: string
  store_open_time: string
  store_close_time: string
  after_hours_default_window_minutes: string
  app_store_url: string
  app_store_provider_token: string
  play_store_url: string
  ambassador_app_store_url: string
  ambassador_play_store_url: string
  gift_wrap_enabled: string
  gift_wrap_price: string
  drops_account_creation: string
  birthday_gifts_enabled: string
  birthday_gift_pct: string
  birthday_gift_expiry_days: string
  drops_order_earn_rate: string
  drops_referral_award: string
  drops_referral_bonus: string
  free_delivery_threshold: string
  delivery_fee_fils: string
  driver_base_fare_fils: string
  driver_per_km_fils: string
  store_origin_lat: string
  store_origin_lng: string
  delivery_sla_minutes: string
  dispatch_tier_1_km: string
  dispatch_tier_2_km: string
  dispatch_tier_3_km: string
  dispatch_max_radius_km: string
  dispatch_escalation_minutes: string
  dispatch_broadcast_all_when_online_lte: string
  tier_aged_min_points: string
  tier_vintage_min_points: string
  tier_aged_free_delivery_fils: string
  tier_vintage_free_delivery_fils: string
  tier_aged_drops_multiplier_pct: string
  tier_vintage_drops_multiplier_pct: string
  vip_discount_pct: string
  catering_setup_fee_fils: string
  catering_per_hour_fils: string
  catering_per_bartender_per_hour_fils: string
  catering_pkg_soft_per_guest_fils: string
  catering_pkg_beer_wine_per_guest_fils: string
  catering_pkg_standard_per_guest_fils: string
  catering_pkg_premium_per_guest_fils: string
  ai_enabled: string
  ai_voice_enabled: string
  cybersource_enabled: string
  ai_hourly_limit: string
  ai_daily_budget_usd: string
  rec_engine_enabled: string
  rec_engine_daily_budget_usd: string
  rec_engine_house_brand_boost: string
  rec_engine_cadence: string
  // POS discount thresholds + VAT (migration 0108). Manager-PIN gate.
  pos_discount_no_pin_max_pct: string
  pos_discount_no_pin_max_fils: string
  pos_vat_exempt_pct: string
  pos_vat_exempt_require_pin: string
  pos_self_serve_void_window_minutes: string
  pos_shift_variance_no_pin_max_fils: string
  // Phone-in pay-by-link expiry. When a cashier rings a phone-in order
  // with payment_method='card', the customer gets an SMS with a payment
  // link that expires after this many minutes; cron auto-cancels the
  // order if payment doesn't land. Default 30.
  phone_in_pay_by_link_expiry_minutes: string
  // In-app gift-card purchase (Payments tab). When on, the customer
  // app's home screen shows a gift-card merchandising card above the
  // shelves. Profile → Gift Cards tile is always on regardless.
  giftcard_buy_home_card_enabled: string
}

type Tab = 'pickup' | 'giftwrap' | 'delivery' | 'drops' | 'tiers' | 'catering' | 'ai' | 'payments' | 'pos' | 'app' | 'whatsapp' | 'klaviyo'

interface KlaviyoStatus {
  api_key_set: boolean
  list_id: string | null
  eligible_customers: number
}

interface WaTemplate {
  name: string
  category: string
  language: string
  hasUrlButton: boolean
  buttonUrl: string | null
  buttonBroken: boolean
  enabled: boolean
}

function SaveRow({ onSave, isPending, saved, error }: {
  onSave: () => void
  isPending: boolean
  saved: boolean
  error: string
}) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <button
        onClick={onSave}
        disabled={isPending}
        className="px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
      >
        {saved ? '✓ Saved' : isPending ? 'Saving…' : 'Save'}
      </button>
      {error && <p className="text-red-400 text-xs">{error}</p>}
    </div>
  )
}

function Toggle({ enabled, onChange, disabled }: { enabled: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
        enabled ? 'bg-[#c41e3a]' : 'bg-white/20'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  )
}

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50'
const labelCls = 'block text-xs text-gray-400 mb-1'
const hintCls  = 'text-xs text-gray-600 mt-1'

export default function Settings() {
  const { user } = useAuth()
  const canManage = user?.role === 'super_admin' || user?.role === 'operations'
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<Tab>('pickup')

  // ── Field state ─────────────────────────────────────────────────────────────
  const [pickupAddress, setPickupAddress]   = useState('')
  const [pickupHours, setPickupHours]       = useState('')
  const [pickupNotes, setPickupNotes]       = useState('')
  const [storeOpenTime, setStoreOpenTime]   = useState('11:00')
  const [storeCloseTime, setStoreCloseTime] = useState('23:00')
  const [afterHoursWindow, setAfterHoursWindow] = useState('120')
  const [giftWrapPrice, setGiftWrapPrice]   = useState('0')
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState('30000')
  const [deliveryFeeFils, setDeliveryFeeFils]             = useState('2000')
  const [driverBaseFareFils, setDriverBaseFareFils]       = useState('1200')
  const [driverPerKmFils, setDriverPerKmFils]             = useState('150')
  const [storeOriginLat, setStoreOriginLat]               = useState('31.9515694')
  const [storeOriginLng, setStoreOriginLng]               = useState('35.9239625')
  const [deliverySlaMinutes, setDeliverySlaMinutes]       = useState('30')
  const [dispatchTier1, setDispatchTier1]                 = useState('5')
  const [dispatchTier2, setDispatchTier2]                 = useState('10')
  const [dispatchTier3, setDispatchTier3]                 = useState('20')
  const [dispatchMaxRadius, setDispatchMaxRadius]         = useState('30')
  const [dispatchEscalationMinutes, setDispatchEscalationMinutes] = useState('3')
  const [dispatchBroadcastAllLte, setDispatchBroadcastAllLte] = useState('3')
  const [tierAgedMinPoints, setTierAgedMinPoints]                       = useState('500')
  const [tierVintageMinPoints, setTierVintageMinPoints]                 = useState('2000')
  const [tierAgedFreeDeliveryFils, setTierAgedFreeDeliveryFils]         = useState('25000')
  const [tierVintageFreeDeliveryFils, setTierVintageFreeDeliveryFils]   = useState('20000')
  const [tierAgedDropsPct, setTierAgedDropsPct]                         = useState('125')
  const [tierVintageDropsPct, setTierVintageDropsPct]                   = useState('150')
  const [vipDiscountPct, setVipDiscountPct]                             = useState('15')
  const [dropsAccountCreation, setDropsAccountCreation]   = useState('50')
  const [dropsOrderEarnRate, setDropsOrderEarnRate]       = useState('1')
  const [dropsReferralAward, setDropsReferralAward]       = useState('25')
  const [dropsReferralBonus, setDropsReferralBonus]       = useState('10')
  const [birthdayEnabled, setBirthdayEnabled]             = useState(false)
  const [birthdayPct, setBirthdayPct]                     = useState('15')
  const [birthdayExpiryDays, setBirthdayExpiryDays]       = useState('30')
  const [cateringSetupFee, setCateringSetupFee]                       = useState('50000')
  const [cateringPerHour, setCateringPerHour]                         = useState('15000')
  const [cateringPerBartenderPerHour, setCateringPerBartenderPerHour] = useState('12000')
  const [cateringPkgSoft, setCateringPkgSoft]             = useState('3000')
  const [cateringPkgBeerWine, setCateringPkgBeerWine]     = useState('12000')
  const [cateringPkgStandard, setCateringPkgStandard]     = useState('20000')
  const [cateringPkgPremium, setCateringPkgPremium]       = useState('35000')
  const [aiHourlyLimit, setAiHourlyLimit]                 = useState('10')
  const [savedAiLimit, setSavedAiLimit]                   = useState(false)
  const [errorAiLimit, setErrorAiLimit]                   = useState('')
  const [aiDailyBudget, setAiDailyBudget]                 = useState('20')
  const [savedAiBudget, setSavedAiBudget]                 = useState(false)
  const [errorAiBudget, setErrorAiBudget]                 = useState('')
  const [recEngineBudget, setRecEngineBudget]             = useState('10')
  const [savedRecBudget, setSavedRecBudget]               = useState(false)
  const [errorRecBudget, setErrorRecBudget]               = useState('')
  const [recHouseBrandBoost, setRecHouseBrandBoost]       = useState('1.2')
  const [savedRecBoost, setSavedRecBoost]                 = useState(false)
  const [errorRecBoost, setErrorRecBoost]                 = useState('')
  const [recCadence, setRecCadence]                       = useState('weekly')
  const [savedRecCadence, setSavedRecCadence]             = useState(false)
  const [errorRecCadence, setErrorRecCadence]             = useState('')
  // POS (migration 0108 — manager-PIN gate + VAT exempt + 2c self-serve void window)
  const [posDiscountMaxPct, setPosDiscountMaxPct]         = useState('10')
  // Stored server-side as fils; the admin form works in JD so ops
  // never has to think in fils (matches the shift-variance field).
  const [posDiscountMaxJd, setPosDiscountMaxJd]           = useState('5.000')
  const [posVatExemptPct, setPosVatExemptPct]             = useState('16')
  const [posVatRequirePin, setPosVatRequirePin]           = useState(false)
  const [posVoidWindowMinutes, setPosVoidWindowMinutes]   = useState('5')
  // W4 shift-close cash variance threshold — stored as fils server-side but
  // the input takes JD for cashier-readable units. Default JD 5.000.
  const [posShiftVarianceMaxJd, setPosShiftVarianceMaxJd] = useState('5.000')
  // Phone-in pay-by-link expiry (minutes). Default 30; admin can tune
  // longer for slower-paying customer bases or shorter for tighter ops.
  const [phoneInPayLinkExpiryMin, setPhoneInPayLinkExpiryMin] = useState('30')
  const [savedPos, setSavedPos]                           = useState(false)
  const [errorPos, setErrorPos]                           = useState('')

  const [appStoreUrl, setAppStoreUrl]                     = useState('')
  const [appStoreProviderToken, setAppStoreProviderToken] = useState('')
  const [playStoreUrl, setPlayStoreUrl]                   = useState('')
  const [ambAppStoreUrl, setAmbAppStoreUrl]               = useState('')
  const [ambPlayStoreUrl, setAmbPlayStoreUrl]             = useState('')
  const [savedAppLinks, setSavedAppLinks]                 = useState(false)
  const [errorAppLinks, setErrorAppLinks]                 = useState('')
  const [savedAmbLinks, setSavedAmbLinks]                 = useState(false)
  const [errorAmbLinks, setErrorAmbLinks]                 = useState('')

  // ── Per-tab save feedback ────────────────────────────────────────────────────
  const [savedPickup,   setSavedPickup]   = useState(false)
  const [savedGiftWrap, setSavedGiftWrap] = useState(false)
  const [savedDelivery, setSavedDelivery] = useState(false)
  const [savedPayout,   setSavedPayout]   = useState(false)
  const [savedDispatch, setSavedDispatch] = useState(false)
  const [savedDrops,    setSavedDrops]    = useState(false)
  const [savedTiers,    setSavedTiers]    = useState(false)
  const [savedCatering, setSavedCatering] = useState(false)
  const [errorPickup,   setErrorPickup]   = useState('')
  const [errorGiftWrap, setErrorGiftWrap] = useState('')
  const [errorDelivery, setErrorDelivery] = useState('')
  const [errorPayout,   setErrorPayout]   = useState('')
  const [errorDispatch, setErrorDispatch] = useState('')
  const [errorDrops,    setErrorDrops]    = useState('')
  const [errorTiers,    setErrorTiers]    = useState('')
  const [errorCatering, setErrorCatering] = useState('')

  const { data, isLoading } = useQuery<{ settings: StoreSettings }>({
    queryKey: ['store-settings'],
    queryFn: () => api.get('/settings').then(r => r.data),
  })

  useEffect(() => {
    if (!data) return
    setPickupAddress(data.settings.pickup_address ?? '')
    setPickupHours(data.settings.pickup_hours ?? '')
    setPickupNotes(data.settings.pickup_notes ?? '')
    setStoreOpenTime(data.settings.store_open_time ?? '11:00')
    setStoreCloseTime(data.settings.store_close_time ?? '23:00')
    setAfterHoursWindow(data.settings.after_hours_default_window_minutes ?? '120')
    setGiftWrapPrice(data.settings.gift_wrap_price ?? '0')
    setFreeDeliveryThreshold(data.settings.free_delivery_threshold ?? '30000')
    setDeliveryFeeFils(data.settings.delivery_fee_fils ?? '2000')
    setDriverBaseFareFils(data.settings.driver_base_fare_fils ?? '1200')
    setDriverPerKmFils(data.settings.driver_per_km_fils ?? '150')
    setStoreOriginLat(data.settings.store_origin_lat ?? '31.9515694')
    setStoreOriginLng(data.settings.store_origin_lng ?? '35.9239625')
    setDeliverySlaMinutes(data.settings.delivery_sla_minutes ?? '30')
    setDispatchTier1(data.settings.dispatch_tier_1_km ?? '5')
    setDispatchTier2(data.settings.dispatch_tier_2_km ?? '10')
    setDispatchTier3(data.settings.dispatch_tier_3_km ?? '20')
    setDispatchMaxRadius(data.settings.dispatch_max_radius_km ?? '30')
    setDispatchEscalationMinutes(data.settings.dispatch_escalation_minutes ?? '3')
    setDispatchBroadcastAllLte(data.settings.dispatch_broadcast_all_when_online_lte ?? '3')
    setTierAgedMinPoints(data.settings.tier_aged_min_points ?? '500')
    setTierVintageMinPoints(data.settings.tier_vintage_min_points ?? '2000')
    setTierAgedFreeDeliveryFils(data.settings.tier_aged_free_delivery_fils ?? '25000')
    setTierVintageFreeDeliveryFils(data.settings.tier_vintage_free_delivery_fils ?? '20000')
    setTierAgedDropsPct(data.settings.tier_aged_drops_multiplier_pct ?? '125')
    setTierVintageDropsPct(data.settings.tier_vintage_drops_multiplier_pct ?? '150')
    setVipDiscountPct(data.settings.vip_discount_pct ?? '15')
    setDropsAccountCreation(data.settings.drops_account_creation ?? '50')
    setBirthdayEnabled((data.settings.birthday_gifts_enabled ?? '0') === '1')
    setBirthdayPct(data.settings.birthday_gift_pct ?? '15')
    setBirthdayExpiryDays(data.settings.birthday_gift_expiry_days ?? '30')
    setDropsOrderEarnRate(data.settings.drops_order_earn_rate ?? '1')
    setDropsReferralAward(data.settings.drops_referral_award ?? '25')
    setDropsReferralBonus(data.settings.drops_referral_bonus ?? '10')
    setCateringSetupFee(data.settings.catering_setup_fee_fils ?? '50000')
    setCateringPerHour(data.settings.catering_per_hour_fils ?? '15000')
    setCateringPerBartenderPerHour(data.settings.catering_per_bartender_per_hour_fils ?? '12000')
    setCateringPkgSoft(data.settings.catering_pkg_soft_per_guest_fils ?? '3000')
    setCateringPkgBeerWine(data.settings.catering_pkg_beer_wine_per_guest_fils ?? '12000')
    setCateringPkgStandard(data.settings.catering_pkg_standard_per_guest_fils ?? '20000')
    setCateringPkgPremium(data.settings.catering_pkg_premium_per_guest_fils ?? '35000')
    setAiHourlyLimit(data.settings.ai_hourly_limit ?? '10')
    setAiDailyBudget(data.settings.ai_daily_budget_usd ?? '20')
    setRecEngineBudget(data.settings.rec_engine_daily_budget_usd ?? '10')
    setRecHouseBrandBoost(data.settings.rec_engine_house_brand_boost ?? '1.2')
    setRecCadence(data.settings.rec_engine_cadence ?? 'weekly')
    setPosDiscountMaxPct(data.settings.pos_discount_no_pin_max_pct ?? '10')
    // fils → JD for the input. `Number.isFinite` (not `||`) so a
    // legitimately saved 0 doesn't get silently replaced by the default.
    const discountFils = parseInt(data.settings.pos_discount_no_pin_max_fils ?? '5000', 10)
    setPosDiscountMaxJd((Number.isFinite(discountFils) ? discountFils / 1000 : 5).toFixed(3))
    setPosVatExemptPct(data.settings.pos_vat_exempt_pct ?? '16')
    setPosVatRequirePin(data.settings.pos_vat_exempt_require_pin === 'true')
    setPosVoidWindowMinutes(data.settings.pos_self_serve_void_window_minutes ?? '5')
    // fils -> JD for the input. `Number.isFinite` (not `||`) so a
    // legitimately saved 0 (manager wants a PIN on any variance) doesn't
    // get silently replaced by the 5000-fils default on reload.
    const varianceFils = parseInt(data.settings.pos_shift_variance_no_pin_max_fils ?? '5000', 10)
    setPosShiftVarianceMaxJd((Number.isFinite(varianceFils) ? varianceFils / 1000 : 5).toFixed(3))
    setPhoneInPayLinkExpiryMin(data.settings.phone_in_pay_by_link_expiry_minutes ?? '30')
    setAppStoreUrl(data.settings.app_store_url ?? '')
    setAppStoreProviderToken(data.settings.app_store_provider_token ?? '')
    setPlayStoreUrl(data.settings.play_store_url ?? '')
    setAmbAppStoreUrl(data.settings.ambassador_app_store_url ?? '')
    setAmbPlayStoreUrl(data.settings.ambassador_play_store_url ?? '')
  }, [data])

  const pickupEnabled   = data?.settings.pickup_enabled   === '1'
  const giftWrapEnabled = data?.settings.gift_wrap_enabled === '1'
  // AI defaults to ON when unset — existing deployments stay enabled.
  const aiEnabled       = data?.settings.ai_enabled !== '0'
  const aiVoiceEnabled  = data?.settings.ai_voice_enabled === '1'
  const cybersourceEnabled = data?.settings.cybersource_enabled === '1'
  const giftcardHomeCardEnabled = data?.settings.giftcard_buy_home_card_enabled === '1'
  const recEngineEnabled = data?.settings.rec_engine_enabled !== '0'

  const patch = useMutation({
    mutationFn: (payload: Record<string, string | boolean | number>) =>
      api.patch('/settings', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store-settings'] }),
  })

  // WhatsApp templates (enable/disable) — fetched from Meta, only when the
  // tab is open so we don't hit the Graph API on every Settings visit.
  const waTemplatesQuery = useQuery<{ templates: WaTemplate[]; configured: boolean }>({
    queryKey: ['wa-templates-settings'],
    queryFn: () => api.get('/settings/whatsapp-templates').then(r => r.data),
    enabled: activeTab === 'whatsapp',
  })
  const waToggle = useMutation({
    mutationFn: (v: { name: string; enabled: boolean }) =>
      api.post('/settings/whatsapp-templates', v).then(r => r.data),
    onSuccess: () => waTemplatesQuery.refetch(),
  })

  // Klaviyo email-marketing sync — status + one-click push.
  const klaviyoStatus = useQuery<KlaviyoStatus>({
    queryKey: ['klaviyo-status'],
    queryFn: () => api.get('/klaviyo/status').then(r => r.data),
    enabled: activeTab === 'klaviyo',
  })
  const klaviyoSync = useMutation({
    mutationFn: () => api.post('/klaviyo/sync', {}).then(r => r.data as { eligible: number; synced: number; failed: number }),
  })

  const save = (
    payload: Record<string, string | boolean | number>,
    setSaved: (v: boolean) => void,
    setError: (v: string) => void,
  ) => {
    setError('')
    patch.mutate(payload, {
      onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000) },
      onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
    })
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'pickup',   label: 'Store & Pickup',  icon: <Package    size={14} /> },
    { id: 'giftwrap', label: 'Gift Wrap',        icon: <Gift       size={14} /> },
    { id: 'delivery', label: 'Delivery Fees',    icon: <Truck      size={14} /> },
    { id: 'drops',    label: 'Drops Economy',    icon: <Droplets   size={14} /> },
    { id: 'tiers',    label: 'Loyalty Tiers',    icon: <Award      size={14} /> },
    { id: 'catering', label: 'Catering',         icon: <Wine       size={14} /> },
    { id: 'ai',       label: 'DrinX AI',         icon: <Sparkles   size={14} /> },
    { id: 'payments', label: 'Payments',         icon: <CreditCard size={14} /> },
    { id: 'pos',      label: 'POS',              icon: <Lock       size={14} /> },
    { id: 'app',      label: 'App Links',        icon: <Smartphone size={14} /> },
    { id: 'whatsapp', label: 'WhatsApp',         icon: <MessageSquare size={14} /> },
    { id: 'klaviyo',  label: 'Klaviyo',          icon: <Mail         size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-6">Store Settings</h1>

      {/* Tab bar — wide enough for all labels to fit without scrolling. */}
      <div className="flex gap-1 mb-6 bg-white/5 rounded-xl p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-[#c41e3a] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
      ) : (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">

          {/* ── Store & Pickup ─────────────────────────────────────────────── */}
          {activeTab === 'pickup' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Enable store pickup</p>
                  <p className="text-xs text-gray-500 mt-0.5">Customers can choose pickup at checkout.</p>
                </div>
                {canManage
                  ? <Toggle enabled={pickupEnabled} onChange={() => patch.mutate({ pickup_enabled: !pickupEnabled })} disabled={patch.isPending} />
                  : <span className={`text-xs font-semibold ${pickupEnabled ? 'text-green-400' : 'text-gray-500'}`}>{pickupEnabled ? 'Enabled' : 'Disabled'}</span>
                }
              </div>
              <div>
                <label className={labelCls}>Pickup address</label>
                <input type="text" value={pickupAddress} onChange={e => setPickupAddress(e.target.value)} disabled={!canManage}
                  placeholder="e.g. Sweifieh, Amman — Building 12, Ground Floor" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Store hours (display label)</label>
                <input type="text" value={pickupHours} onChange={e => setPickupHours(e.target.value)} disabled={!canManage}
                  placeholder="e.g. Sun–Thu 10am–10pm, Fri–Sat 12pm–11pm" className={inputCls} />
                <p className="text-[11px] text-gray-500 mt-1">Free-text label shown on the pickup page. Doesn't control delivery cutoffs.</p>
              </div>
              <div>
                <label className={labelCls}>Pickup notes (optional)</label>
                <input type="text" value={pickupNotes} onChange={e => setPickupNotes(e.target.value)} disabled={!canManage}
                  placeholder="e.g. Ring the bell at the side entrance" className={inputCls} />
              </div>

              <div className="pt-4 mt-4 border-t border-gray-800/60">
                <p className="text-sm font-medium text-white">Delivery hours (enforcement)</p>
                <p className="text-xs text-gray-500 mt-0.5 mb-3">
                  Amman local time. Orders placed outside this window are accepted but scheduled for the next opening. Times are inclusive of open, exclusive of close.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>Opening time</label>
                    <input type="time" value={storeOpenTime} onChange={e => setStoreOpenTime(e.target.value)} disabled={!canManage} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Closing time</label>
                    <input type="time" value={storeCloseTime} onChange={e => setStoreCloseTime(e.target.value)} disabled={!canManage} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>After-hours delivery cushion (min)</label>
                    <input type="number" min={30} step={15} value={afterHoursWindow} onChange={e => setAfterHoursWindow(e.target.value)} disabled={!canManage} className={inputCls} />
                    <p className="text-[11px] text-gray-500 mt-1">Slack window ops has to work through the overnight backlog after opening. Customers don't see this number.</p>
                  </div>
                </div>
              </div>

              {canManage && (
                <SaveRow
                  onSave={() => save({
                    pickup_address: pickupAddress,
                    pickup_hours: pickupHours,
                    pickup_notes: pickupNotes,
                    store_open_time: storeOpenTime,
                    store_close_time: storeCloseTime,
                    after_hours_default_window_minutes: afterHoursWindow,
                  }, setSavedPickup, setErrorPickup)}
                  isPending={patch.isPending} saved={savedPickup} error={errorPickup}
                />
              )}
            </div>
          )}

          {/* ── Gift Wrap ──────────────────────────────────────────────────── */}
          {activeTab === 'giftwrap' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Enable gift wrapping</p>
                  <p className="text-xs text-gray-500 mt-0.5">Customers can add premium gift wrapping per item.</p>
                </div>
                {canManage
                  ? <Toggle enabled={giftWrapEnabled} onChange={() => patch.mutate({ gift_wrap_enabled: !giftWrapEnabled })} disabled={patch.isPending} />
                  : <span className={`text-xs font-semibold ${giftWrapEnabled ? 'text-green-400' : 'text-gray-500'}`}>{giftWrapEnabled ? 'Enabled' : 'Disabled'}</span>
                }
              </div>
              <div>
                <label className={labelCls}>Price per item <span className="text-gray-600">(fils — 0 = free)</span></label>
                <div className="flex items-center gap-3">
                  <input type="number" min="0" value={giftWrapPrice} onChange={e => setGiftWrapPrice(e.target.value)} disabled={!canManage}
                    placeholder="0" className="w-40 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50" />
                  <span className="text-xs text-gray-500">
                    {parseInt(giftWrapPrice || '0') === 0 ? 'Free' : `JD ${(parseInt(giftWrapPrice) / 1000).toFixed(3)} per item`}
                  </span>
                </div>
              </div>
              {canManage && (
                <SaveRow
                  onSave={() => save({ gift_wrap_price: Math.max(0, parseInt(giftWrapPrice || '0', 10)) }, setSavedGiftWrap, setErrorGiftWrap)}
                  isPending={patch.isPending} saved={savedGiftWrap} error={errorGiftWrap}
                />
              )}
            </div>
          )}

          {/* ── Delivery Fees ──────────────────────────────────────────────── */}
          {activeTab === 'delivery' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Free delivery threshold <span className="text-gray-600">(fils)</span></label>
                  <input type="number" min="0" value={freeDeliveryThreshold} onChange={e => setFreeDeliveryThreshold(e.target.value)}
                    disabled={!canManage} className={inputCls} />
                  <p className={hintCls}>
                    Orders above this get free delivery.{' '}
                    {parseInt(freeDeliveryThreshold || '0') > 0 && `= JD ${(parseInt(freeDeliveryThreshold) / 1000).toFixed(3)}`}
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Delivery fee <span className="text-gray-600">(fils)</span></label>
                  <input type="number" min="0" value={deliveryFeeFils} onChange={e => setDeliveryFeeFils(e.target.value)}
                    disabled={!canManage} className={inputCls} />
                  <p className={hintCls}>
                    Charged when below threshold.{' '}
                    {parseInt(deliveryFeeFils || '0') > 0 && `= JD ${(parseInt(deliveryFeeFils) / 1000).toFixed(3)}`}
                  </p>
                </div>
              </div>
              {canManage && (
                <SaveRow
                  onSave={() => save({
                    free_delivery_threshold: Math.max(0, parseInt(freeDeliveryThreshold || '0', 10)),
                    delivery_fee_fils: Math.max(0, parseInt(deliveryFeeFils || '0', 10)),
                  }, setSavedDelivery, setErrorDelivery)}
                  isPending={patch.isPending} saved={savedDelivery} error={errorDelivery}
                />
              )}

              <div className="pt-6 mt-2 border-t border-white/10">
                <h3 className="text-sm font-semibold text-white mb-1">Driver payout</h3>
                <p className={hintCls + ' mb-4'}>
                  Drivers are paid base fare + per-km from the store origin. This is what the driver earns —
                  separate from what the customer pays for delivery.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Base fare <span className="text-gray-600">(fils)</span></label>
                    <input type="number" min="0" value={driverBaseFareFils} onChange={e => setDriverBaseFareFils(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      Flat starting fee per delivery.{' '}
                      {parseInt(driverBaseFareFils || '0') > 0 && `= JD ${(parseInt(driverBaseFareFils) / 1000).toFixed(3)}`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Per kilometre <span className="text-gray-600">(fils)</span></label>
                    <input type="number" min="0" value={driverPerKmFils} onChange={e => setDriverPerKmFils(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      Added per km of straight-line distance.{' '}
                      {parseInt(driverPerKmFils || '0') > 0 && `= JD ${(parseInt(driverPerKmFils) / 1000).toFixed(3)} / km`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Store origin latitude</label>
                    <input type="number" step="any" value={storeOriginLat} onChange={e => setStoreOriginLat(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>The dispatch warehouse / store coordinate.</p>
                  </div>
                  <div>
                    <label className={labelCls}>Store origin longitude</label>
                    <input type="number" step="any" value={storeOriginLng} onChange={e => setStoreOriginLng(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Used to compute distance to each delivery.</p>
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>On-time delivery SLA <span className="text-gray-600">(minutes)</span></label>
                    <input type="number" min="1" value={deliverySlaMinutes} onChange={e => setDeliverySlaMinutes(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      Target delivery time from "Out for Delivery" to "Delivered". Used to compute the
                      ambassador's on-time rate on their profile.
                    </p>
                  </div>
                </div>
                {canManage && (
                  <SaveRow
                    onSave={() => save({
                      driver_base_fare_fils: Math.max(0, parseInt(driverBaseFareFils || '0', 10)),
                      driver_per_km_fils:    Math.max(0, parseInt(driverPerKmFils || '0', 10)),
                      store_origin_lat:      parseFloat(storeOriginLat || '0'),
                      store_origin_lng:      parseFloat(storeOriginLng || '0'),
                      delivery_sla_minutes:  Math.max(1, parseInt(deliverySlaMinutes || '30', 10)),
                    }, setSavedPayout, setErrorPayout)}
                    isPending={patch.isPending} saved={savedPayout} error={errorPayout}
                  />
                )}
              </div>

              <div className="pt-6 mt-2 border-t border-white/10">
                <h3 className="text-sm font-semibold text-white mb-1">Dispatch tiers</h3>
                <p className={hintCls + ' mb-4'}>
                  When admin confirms a delivery order, the closest ambassadors are notified first.
                  If no one claims within the escalation interval, the radius expands to the next tier.
                  Distance is measured from the store origin to each ambassador's last reported location.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Tier 1 radius <span className="text-gray-600">(km)</span></label>
                    <input type="number" min="0" value={dispatchTier1} onChange={e => setDispatchTier1(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Closest ambassadors only.</p>
                  </div>
                  <div>
                    <label className={labelCls}>Tier 2 radius <span className="text-gray-600">(km)</span></label>
                    <input type="number" min="0" value={dispatchTier2} onChange={e => setDispatchTier2(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Second ring after escalation.</p>
                  </div>
                  <div>
                    <label className={labelCls}>Tier 3 radius <span className="text-gray-600">(km)</span></label>
                    <input type="number" min="0" value={dispatchTier3} onChange={e => setDispatchTier3(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Third ring.</p>
                  </div>
                  <div>
                    <label className={labelCls}>Max radius <span className="text-gray-600">(km)</span></label>
                    <input type="number" min="0" value={dispatchMaxRadius} onChange={e => setDispatchMaxRadius(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Final ring. After this, broadcast to all online ambassadors.</p>
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Escalation interval <span className="text-gray-600">(minutes)</span></label>
                    <input type="number" min="1" value={dispatchEscalationMinutes} onChange={e => setDispatchEscalationMinutes(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>How long to wait before expanding the radius.</p>
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Notify all when online ≤ <span className="text-gray-600">(ambassadors)</span></label>
                    <input type="number" min="0" value={dispatchBroadcastAllLte} onChange={e => setDispatchBroadcastAllLte(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Small fleet: when this many or fewer ambassadors are online, skip the rings and ping all of them on the first dispatch. 0 = always use the tiered rings.</p>
                  </div>
                </div>
                {canManage && (
                  <SaveRow
                    onSave={() => save({
                      dispatch_tier_1_km:          Math.max(0, parseInt(dispatchTier1 || '0', 10)),
                      dispatch_tier_2_km:          Math.max(0, parseInt(dispatchTier2 || '0', 10)),
                      dispatch_tier_3_km:          Math.max(0, parseInt(dispatchTier3 || '0', 10)),
                      dispatch_max_radius_km:      Math.max(0, parseInt(dispatchMaxRadius || '0', 10)),
                      dispatch_escalation_minutes: Math.max(1, parseInt(dispatchEscalationMinutes || '3', 10)),
                      dispatch_broadcast_all_when_online_lte: Math.max(0, parseInt(dispatchBroadcastAllLte || '0', 10)),
                    }, setSavedDispatch, setErrorDispatch)}
                    isPending={patch.isPending} saved={savedDispatch} error={errorDispatch}
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Drops Economy ──────────────────────────────────────────────── */}
          {activeTab === 'drops' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Account creation bonus <span className="text-gray-600">(drops)</span></label>
                  <input type="number" min="0" value={dropsAccountCreation} onChange={e => setDropsAccountCreation(e.target.value)}
                    disabled={!canManage} className={inputCls} />
                  <p className={hintCls}>Awarded when a new account is created</p>
                </div>
                <div>
                  <label className={labelCls}>Order earn rate <span className="text-gray-600">(drops per 1,000 fils)</span></label>
                  <input type="number" min="0" value={dropsOrderEarnRate} onChange={e => setDropsOrderEarnRate(e.target.value)}
                    disabled={!canManage} className={inputCls} />
                  <p className={hintCls}>e.g. 1 = 1 drop per JD 1.000 spent</p>
                </div>
                <div>
                  <label className={labelCls}>Referral award <span className="text-gray-600">(to referrer)</span></label>
                  <input type="number" min="0" value={dropsReferralAward} onChange={e => setDropsReferralAward(e.target.value)}
                    disabled={!canManage} className={inputCls} />
                  <p className={hintCls}>Awarded when the referred user places their first order</p>
                </div>
                <div>
                  <label className={labelCls}>Referral bonus <span className="text-gray-600">(to new user)</span></label>
                  <input type="number" min="0" value={dropsReferralBonus} onChange={e => setDropsReferralBonus(e.target.value)}
                    disabled={!canManage} className={inputCls} />
                  <p className={hintCls}>Awarded to the referred user immediately on sign-up, on top of the account-creation bonus above</p>
                </div>
              </div>
              <div className="pt-5 mt-2 border-t border-gray-800/60">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-white">Birthday gifts</p>
                    <p className="text-xs text-gray-500 mt-0.5">Once per year on the customer&apos;s DOB, we mint a single-use % code + push + SMS. Uses the DOB already collected at the age gate.</p>
                  </div>
                  {canManage
                    ? <Toggle enabled={birthdayEnabled} onChange={() => setBirthdayEnabled(v => !v)} disabled={patch.isPending} />
                    : <span className={`text-xs font-semibold ${birthdayEnabled ? 'text-green-400' : 'text-gray-500'}`}>{birthdayEnabled ? 'Enabled' : 'Disabled'}</span>
                  }
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Discount % <span className="text-gray-600">(1–50)</span></label>
                    <input type="number" min={1} max={50} value={birthdayPct} onChange={e => setBirthdayPct(e.target.value)} disabled={!canManage || !birthdayEnabled} className={inputCls} />
                    <p className={hintCls}>Percentage off the next order</p>
                  </div>
                  <div>
                    <label className={labelCls}>Code expiry <span className="text-gray-600">(days, 1–90)</span></label>
                    <input type="number" min={1} max={90} value={birthdayExpiryDays} onChange={e => setBirthdayExpiryDays(e.target.value)} disabled={!canManage || !birthdayEnabled} className={inputCls} />
                    <p className={hintCls}>How long the code stays valid after birthday</p>
                  </div>
                </div>
              </div>

              {canManage && (
                <SaveRow
                  onSave={() => save({
                    drops_account_creation: Math.max(0, parseInt(dropsAccountCreation || '0', 10)),
                    drops_order_earn_rate:  Math.max(0, parseInt(dropsOrderEarnRate  || '0', 10)),
                    drops_referral_award:   Math.max(0, parseInt(dropsReferralAward  || '0', 10)),
                    drops_referral_bonus:   Math.max(0, parseInt(dropsReferralBonus  || '0', 10)),
                    birthday_gifts_enabled: birthdayEnabled ? '1' : '0',
                    birthday_gift_pct: Math.max(1, Math.min(50, parseInt(birthdayPct || '15', 10))),
                    birthday_gift_expiry_days: Math.max(1, Math.min(90, parseInt(birthdayExpiryDays || '30', 10))),
                  }, setSavedDrops, setErrorDrops)}
                  isPending={patch.isPending} saved={savedDrops} error={errorDrops}
                />
              )}
            </div>
          )}

          {/* ── Loyalty Tiers ──────────────────────────────────────────────── */}
          {activeTab === 'tiers' && (
            <div className="space-y-6">
              <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
                <p className="text-xs text-gray-400 leading-relaxed">
                  Three tiers themed on whisky maturation: <span className="text-white font-semibold">Bottled</span> (entry, default for everyone) →
                  <span className="text-white font-semibold"> Aged</span> (مُعتَّق) →
                  <span className="text-white font-semibold"> Vintage</span> (نادر).
                  A user's tier is computed from their loyalty points; benefits below override the standard
                  free-delivery threshold and drops earn rate when they hit the threshold.
                </p>
              </div>

              {/* Aged tier */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="text-amber-400">معتّق</span> Aged
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Threshold <span className="text-gray-600">(points)</span></label>
                    <input type="number" min="0" value={tierAgedMinPoints} onChange={e => setTierAgedMinPoints(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Reach this many loyalty points to enter Aged.</p>
                  </div>
                  <div>
                    <label className={labelCls}>Free delivery threshold <span className="text-gray-600">(fils)</span></label>
                    <input type="number" min="0" value={tierAgedFreeDeliveryFils} onChange={e => setTierAgedFreeDeliveryFils(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(tierAgedFreeDeliveryFils || '0') > 0 && `JD ${(parseInt(tierAgedFreeDeliveryFils) / 1000).toFixed(3)}`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Drops multiplier <span className="text-gray-600">(%)</span></label>
                    <input type="number" min="100" value={tierAgedDropsPct} onChange={e => setTierAgedDropsPct(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>e.g. 125 = 1.25× drops earned per order.</p>
                  </div>
                </div>
              </div>

              {/* Vintage tier */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="text-[#C41E3A]">نادر</span> Vintage
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Threshold <span className="text-gray-600">(points)</span></label>
                    <input type="number" min="0" value={tierVintageMinPoints} onChange={e => setTierVintageMinPoints(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Reach this many loyalty points to enter Vintage.</p>
                  </div>
                  <div>
                    <label className={labelCls}>Free delivery threshold <span className="text-gray-600">(fils)</span></label>
                    <input type="number" min="0" value={tierVintageFreeDeliveryFils} onChange={e => setTierVintageFreeDeliveryFils(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(tierVintageFreeDeliveryFils || '0') > 0 && `JD ${(parseInt(tierVintageFreeDeliveryFils) / 1000).toFixed(3)}`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Drops multiplier <span className="text-gray-600">(%)</span></label>
                    <input type="number" min="100" value={tierVintageDropsPct} onChange={e => setTierVintageDropsPct(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>e.g. 150 = 1.5× drops earned per order.</p>
                  </div>
                </div>
              </div>

              {/* VIP auto-discount */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="text-[#C41E3A]">★</span> VIP discount
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>VIP discount <span className="text-gray-600">(%)</span></label>
                    <input type="number" min="0" max="100" value={vipDiscountPct} onChange={e => setVipDiscountPct(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>Automatic discount for VIP customers, applied best-of against any discount code. Set VIP status per customer on the Customers page (super-admin only).</p>
                  </div>
                </div>
              </div>

              {canManage && (
                <SaveRow
                  onSave={() => save({
                    tier_aged_min_points:              Math.max(0, parseInt(tierAgedMinPoints              || '0', 10)),
                    tier_vintage_min_points:           Math.max(0, parseInt(tierVintageMinPoints           || '0', 10)),
                    tier_aged_free_delivery_fils:      Math.max(0, parseInt(tierAgedFreeDeliveryFils       || '0', 10)),
                    tier_vintage_free_delivery_fils:   Math.max(0, parseInt(tierVintageFreeDeliveryFils    || '0', 10)),
                    tier_aged_drops_multiplier_pct:    Math.max(100, parseInt(tierAgedDropsPct             || '100', 10)),
                    tier_vintage_drops_multiplier_pct: Math.max(100, parseInt(tierVintageDropsPct          || '100', 10)),
                    vip_discount_pct:                  Math.max(0, Math.min(100, parseInt(vipDiscountPct   || '15', 10))),
                  }, setSavedTiers, setErrorTiers)}
                  isPending={patch.isPending} saved={savedTiers} error={errorTiers}
                />
              )}
            </div>
          )}

          {/* ── Catering & Bar Service ─────────────────────────────────────── */}
          {activeTab === 'catering' && (
            <div className="space-y-6">
              <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
                <p className="text-xs text-gray-400 leading-relaxed">
                  Pricing for the customer-facing catering form. The estimate shown to the customer is:
                  <span className="text-white"> setup fee + (guests × package per-guest) + (hours × per-hour) + (bartenders × hours × per-bartender per-hour)</span>.
                  All figures are in fils (1 JOD = 1000 fils).
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Base costs</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Setup fee <span className="text-gray-600">(fils)</span></label>
                    <input type="number" min="0" value={cateringSetupFee} onChange={e => setCateringSetupFee(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(cateringSetupFee || '0') > 0 && `JD ${(parseInt(cateringSetupFee) / 1000).toFixed(3)}`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Per-hour overhead <span className="text-gray-600">(fils)</span></label>
                    <input type="number" min="0" value={cateringPerHour} onChange={e => setCateringPerHour(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(cateringPerHour || '0') > 0 && `JD ${(parseInt(cateringPerHour) / 1000).toFixed(3)} / hr`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Bartender rate <span className="text-gray-600">(fils / hr)</span></label>
                    <input type="number" min="0" value={cateringPerBartenderPerHour} onChange={e => setCateringPerBartenderPerHour(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(cateringPerBartenderPerHour || '0') > 0 && `JD ${(parseInt(cateringPerBartenderPerHour) / 1000).toFixed(3)} per bartender / hr`}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Drink packages <span className="text-gray-500 font-normal">(per guest)</span></h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Soft drinks only</label>
                    <input type="number" min="0" value={cateringPkgSoft} onChange={e => setCateringPkgSoft(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(cateringPkgSoft || '0') > 0 && `JD ${(parseInt(cateringPkgSoft) / 1000).toFixed(3)} / guest`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Beer & wine</label>
                    <input type="number" min="0" value={cateringPkgBeerWine} onChange={e => setCateringPkgBeerWine(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(cateringPkgBeerWine || '0') > 0 && `JD ${(parseInt(cateringPkgBeerWine) / 1000).toFixed(3)} / guest`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Standard spirits</label>
                    <input type="number" min="0" value={cateringPkgStandard} onChange={e => setCateringPkgStandard(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(cateringPkgStandard || '0') > 0 && `JD ${(parseInt(cateringPkgStandard) / 1000).toFixed(3)} / guest`}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Premium open bar</label>
                    <input type="number" min="0" value={cateringPkgPremium} onChange={e => setCateringPkgPremium(e.target.value)}
                      disabled={!canManage} className={inputCls} />
                    <p className={hintCls}>
                      {parseInt(cateringPkgPremium || '0') > 0 && `JD ${(parseInt(cateringPkgPremium) / 1000).toFixed(3)} / guest`}
                    </p>
                  </div>
                </div>
              </div>

              {canManage && (
                <SaveRow
                  onSave={() => save({
                    catering_setup_fee_fils:               Math.max(0, parseInt(cateringSetupFee              || '0', 10)),
                    catering_per_hour_fils:                Math.max(0, parseInt(cateringPerHour               || '0', 10)),
                    catering_per_bartender_per_hour_fils:  Math.max(0, parseInt(cateringPerBartenderPerHour   || '0', 10)),
                    catering_pkg_soft_per_guest_fils:      Math.max(0, parseInt(cateringPkgSoft               || '0', 10)),
                    catering_pkg_beer_wine_per_guest_fils: Math.max(0, parseInt(cateringPkgBeerWine           || '0', 10)),
                    catering_pkg_standard_per_guest_fils:  Math.max(0, parseInt(cateringPkgStandard           || '0', 10)),
                    catering_pkg_premium_per_guest_fils:   Math.max(0, parseInt(cateringPkgPremium            || '0', 10)),
                  }, setSavedCatering, setErrorCatering)}
                  isPending={patch.isPending} saved={savedCatering} error={errorCatering}
                />
              )}
            </div>
          )}

          {/* ── DrinX AI ───────────────────────────────────────────────────── */}
          {activeTab === 'ai' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">Enable DrinX AI</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Conversational product recommendations powered by Claude. Disabling hides the
                    AI launcher in the customer app and rejects any in-flight requests.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {canManage
                    ? <Toggle enabled={aiEnabled} onChange={() => patch.mutate({ ai_enabled: !aiEnabled })} disabled={patch.isPending} />
                    : <span className={`text-xs font-semibold ${aiEnabled ? 'text-green-400' : 'text-gray-500'}`}>{aiEnabled ? 'Enabled' : 'Disabled'}</span>
                  }
                </div>
              </div>

              <div className="flex items-center justify-between gap-6 border-t border-white/5 pt-5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">Voice input (mic → Whisper)</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Adds a mic button next to the AI text field. Press-and-hold to record, release to
                    transcribe via Cloudflare Workers AI (Whisper large-v3-turbo, Arabic + English).
                    Off by default while the client-side capture UX is being validated. Rate-limited
                    by the same hourly bucket as text queries.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {canManage
                    ? <Toggle enabled={aiVoiceEnabled} onChange={() => patch.mutate({ ai_voice_enabled: !aiVoiceEnabled })} disabled={patch.isPending} />
                    : <span className={`text-xs font-semibold ${aiVoiceEnabled ? 'text-green-400' : 'text-gray-500'}`}>{aiVoiceEnabled ? 'Enabled' : 'Disabled'}</span>
                  }
                </div>
              </div>

              <div className="border-t border-white/5 pt-5">
                <label className={labelCls}>Per-user hourly conversation limit</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number" min="1" max="1000"
                    value={aiHourlyLimit}
                    onChange={e => setAiHourlyLimit(e.target.value)}
                    disabled={!canManage}
                    className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-500">conversations / user / hour</span>
                </div>
                <p className={hintCls}>
                  When a user hits the cap, the AI tells them when their bucket resets (top of next hour).
                  Raise this if you're confident usage stays within the $20/day global ceiling. Range 1–1000.
                </p>
                {canManage && (
                  <SaveRow
                    onSave={() => save(
                      { ai_hourly_limit: Math.max(1, Math.min(1000, parseInt(aiHourlyLimit || '10', 10))) },
                      setSavedAiLimit, setErrorAiLimit,
                    )}
                    isPending={patch.isPending} saved={savedAiLimit} error={errorAiLimit}
                  />
                )}
              </div>

              <div className="border-t border-white/5 pt-5">
                <label className={labelCls}>Daily budget ceiling (USD)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number" min="0" step="1"
                    value={aiDailyBudget}
                    onChange={e => setAiDailyBudget(e.target.value)}
                    disabled={!canManage}
                    className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-500">USD / day across all concierge calls</span>
                </div>
                <p className={hintCls}>
                  Hard ceiling. Once today's concierge spend reaches this, new AI requests return a polite
                  "try again tomorrow" message. Default $20 — raise if you're confident usage is healthy,
                  lower as a safety brake.
                </p>
                {canManage && (
                  <SaveRow
                    onSave={() => save(
                      { ai_daily_budget_usd: String(Math.max(0, parseFloat(aiDailyBudget || '20') || 20)) },
                      setSavedAiBudget, setErrorAiBudget,
                    )}
                    isPending={patch.isPending} saved={savedAiBudget} error={errorAiBudget}
                  />
                )}
              </div>

              <div className="text-xs text-gray-500 leading-relaxed border-t border-white/5 pt-4">
                <p className="font-semibold text-gray-400 mb-2">Operational notes</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Live usage and cost are visible in the <span className="text-gray-300">DrinX AI</span> page under Insights.</li>
                </ul>
              </div>

              {/* ── Recommendations engine ───────────────────────────────── */}
              <div className="border-t border-white/5 pt-5 space-y-4">
                <div className="flex items-center justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">Recommendations engine</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      Daily LLM-augmented product picks shown on the home rail, product detail page,
                      and order confirmation. Pre-computed by a 3 AM Amman cron + on each new order.
                      Falls back to SQL-only ranking when the budget is exhausted.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    {canManage
                      ? <Toggle enabled={recEngineEnabled} onChange={() => patch.mutate({ rec_engine_enabled: !recEngineEnabled })} disabled={patch.isPending} />
                      : <span className={`text-xs font-semibold ${recEngineEnabled ? 'text-green-400' : 'text-gray-500'}`}>{recEngineEnabled ? 'Enabled' : 'Disabled'}</span>
                    }
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Batch cadence</label>
                  <div className="flex items-center gap-3">
                    <select
                      value={recCadence}
                      onChange={e => setRecCadence(e.target.value)}
                      disabled={!canManage}
                      className="w-40 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly (Mondays)</option>
                      <option value="biweekly">Biweekly</option>
                      <option value="monthly">Monthly (1st)</option>
                    </select>
                    <span className="text-xs text-gray-500">when the full batch runs</span>
                  </div>
                  <p className={hintCls}>
                    Weekly is the default — captures seasonal shifts and post-order recomputes still keep
                    each user's picks fresh after every purchase. Daily costs 7× more for marginal gain;
                    biweekly / monthly save more but can feel stale during a season change.
                  </p>
                  {canManage && (
                    <SaveRow
                      onSave={() => save(
                        { rec_engine_cadence: recCadence },
                        setSavedRecCadence, setErrorRecCadence,
                      )}
                      isPending={patch.isPending} saved={savedRecCadence} error={errorRecCadence}
                    />
                  )}
                </div>

                <div className="border-t border-white/5 pt-5">
                  <label className={labelCls}>Daily LLM budget (USD)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number" min="0" step="0.5"
                      value={recEngineBudget}
                      onChange={e => setRecEngineBudget(e.target.value)}
                      disabled={!canManage}
                      className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                    />
                    <span className="text-xs text-gray-500">USD / batch run</span>
                  </div>
                  <p className={hintCls}>
                    Separate from the concierge ceiling so the two AI features don't compete.
                    Once exhausted, the batch falls back to SQL-only ranking for remaining users.
                  </p>
                  {canManage && (
                    <SaveRow
                      onSave={() => save(
                        { rec_engine_daily_budget_usd: String(Math.max(0, parseFloat(recEngineBudget || '10') || 10)) },
                        setSavedRecBudget, setErrorRecBudget,
                      )}
                      isPending={patch.isPending} saved={savedRecBudget} error={errorRecBudget}
                    />
                  )}
                </div>

                <div className="border-t border-white/5 pt-5">
                  <label className={labelCls}>House-brand candidate boost (×)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number" min="1" max="3" step="0.1"
                      value={recHouseBrandBoost}
                      onChange={e => setRecHouseBrandBoost(e.target.value)}
                      disabled={!canManage}
                      className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-50"
                    />
                    <span className="text-xs text-gray-500">multiplier on YHC / Fursan scores</span>
                  </div>
                  <p className={hintCls}>
                    Soft margin lever: multiplies the candidate score of every house-brand product before ranking.
                    <span className="block mt-1">
                      <span className="text-gray-400">1.0</span> = neutral.{' '}
                      <span className="text-gray-400">1.2</span> = ~20% lift (default — house brands surface more often without crowding out name brands).{' '}
                      <span className="text-gray-400">2.0+</span> = aggressive — house brands dominate the rail. Clamped to 1.0–3.0.
                    </span>
                  </p>
                  {canManage && (
                    <SaveRow
                      onSave={() => save(
                        { rec_engine_house_brand_boost: String(Math.max(1, Math.min(3, parseFloat(recHouseBrandBoost || '1.2') || 1.2))) },
                        setSavedRecBoost, setErrorRecBoost,
                      )}
                      isPending={patch.isPending} saved={savedRecBoost} error={errorRecBoost}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Payments ──────────────────────────────────────────────────── */}
          {activeTab === 'payments' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">Enable card payments</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Card payments via CyberSource (Arab Bank's gateway). When off, the customer
                    app hides the Card option entirely and the worker rejects any card calls.
                    Leave off until production merchant credentials are rotated into the
                    <code className="mx-1 px-1 py-0.5 rounded bg-white/5 text-[10px]">CYBERSOURCE_*</code>
                    secrets — until then the path runs against CyberSource's sandbox.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {canManage
                    ? <Toggle enabled={cybersourceEnabled} onChange={() => patch.mutate({ cybersource_enabled: !cybersourceEnabled })} disabled={patch.isPending} />
                    : <span className={`text-xs font-semibold ${cybersourceEnabled ? 'text-green-400' : 'text-gray-500'}`}>{cybersourceEnabled ? 'Enabled' : 'Disabled'}</span>
                  }
                </div>
              </div>
              <div className="rounded-lg border border-white/5 bg-white/5 px-4 py-3 text-xs text-gray-400 leading-relaxed">
                <p className="font-semibold text-gray-300 mb-1">Status</p>
                <p>
                  CyberSource environment:&nbsp;
                  <span className="text-white">sandbox</span> &nbsp;·&nbsp;
                  Saved cards via TMS:&nbsp;
                  <span className="text-white">supported</span> &nbsp;·&nbsp;
                  PCI scope:&nbsp;
                  <span className="text-white">SAQ-A-EP</span>
                </p>
                <p className="mt-2">
                  CliQ and Apple Pay remain pending separate gateway integrations.
                </p>
              </div>

              {/* In-app gift-card purchase — home merchandising toggle. */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Show "Buy a gift card" card on home</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      When on, the customer app's home screen shows a crimson merchandising card above the
                      product shelves inviting customers to send a DrinX gift card by SMS or email. Profile →
                      Gift Cards is always visible regardless. Requires card payments (above) to be enabled —
                      the card is hidden client-side if CyberSource is off. Bump the toggle around holiday
                      seasons and back off in quieter months.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    {canManage
                      ? <Toggle enabled={giftcardHomeCardEnabled} onChange={() => patch.mutate({ giftcard_buy_home_card_enabled: !giftcardHomeCardEnabled })} disabled={patch.isPending} />
                      : <span className={`text-xs font-semibold ${giftcardHomeCardEnabled ? 'text-green-400' : 'text-gray-500'}`}>{giftcardHomeCardEnabled ? 'Shown' : 'Hidden'}</span>
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── POS (manager-PIN gate + VAT + void window) ────────────────── */}
          {activeTab === 'pos' && (
            <div className="space-y-6">
              <div className="rounded-lg border border-white/5 bg-white/5 px-4 py-3 text-xs text-gray-400 leading-relaxed">
                <p className="font-semibold text-gray-300 mb-1">How these gates work</p>
                <p>
                  When the cashier-discretion discount (per-line + order-level) exceeds either ceiling below, the till prompts
                  for a manager PIN before completing the sale. Discount codes are pre-authorised and don't count. VAT exempt
                  is gated separately. Per-admin PINs are set under Profile → POS Manager PIN. Every override is audit-logged
                  as <code>pos.discount_override</code> with the authorising admin. Threshold semantics live in
                  <code>backend/worker/src/routes/cashier/sales.ts</code>.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>No-PIN ceiling — % of subtotal</label>
                  <input
                    type="number" min="0" max="100"
                    className={inputCls}
                    value={posDiscountMaxPct}
                    onChange={e => setPosDiscountMaxPct(e.target.value)}
                    disabled={!canManage}
                  />
                  <p className={hintCls}>
                    Cashier discounts ≤ this percentage of the subtotal skip the manager PIN.
                  </p>
                </div>
                <div>
                  <label className={labelCls}>No-PIN ceiling — absolute (JD)</label>
                  <input
                    type="number" min="0" max="10000" step="0.001"
                    className={inputCls}
                    value={posDiscountMaxJd}
                    onChange={e => setPosDiscountMaxJd(e.target.value)}
                    disabled={!canManage}
                  />
                  <p className={hintCls}>
                    Absolute cap in JD. Cashier hits whichever ceiling first — the % above OR this JD limit.
                    So at 20% + JD 1,000 here, a JD 100 basket caps at JD 20 (% wins); a JD 10,000 basket caps
                    at JD 1,000 (absolute wins). Set high enough that the % ceiling normally does the work.
                    Server-clamped to JD 10,000 max as a fat-finger guard.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>VAT exempt — discount %</label>
                  <input
                    type="number" min="0" max="100" step="0.01"
                    className={inputCls}
                    value={posVatExemptPct}
                    onChange={e => setPosVatExemptPct(e.target.value)}
                    disabled={!canManage}
                  />
                  <p className={hintCls}>Jordanian VAT is 16%; only edit if the regulation changes.</p>
                </div>
                <div>
                  <label className={labelCls}>Require manager PIN for VAT exempt</label>
                  <div className="flex items-center gap-3 h-[38px]">
                    <Toggle
                      enabled={posVatRequirePin}
                      onChange={() => setPosVatRequirePin(v => !v)}
                      disabled={!canManage}
                    />
                    <span className={`text-xs font-semibold ${posVatRequirePin ? 'text-green-400' : 'text-gray-500'}`}>
                      {posVatRequirePin ? 'PIN required' : 'PIN not required'}
                    </span>
                  </div>
                  <p className={hintCls}>
                    When on, every VAT-exempt sale prompts for a manager PIN regardless of amount. Off today —
                    cashiers can apply VAT exempt themselves for trusted expat / diplomatic customers.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Self-serve void window (minutes)</label>
                  <input
                    type="number" min="0"
                    className={inputCls}
                    value={posVoidWindowMinutes}
                    onChange={e => setPosVoidWindowMinutes(e.target.value)}
                    disabled={!canManage}
                  />
                  <p className={hintCls}>
                    How many minutes after a sale rings up that any active cashier can void it without a manager PIN.
                    Beyond this window, the till prompts for a manager PIN on the Last Sales screen.
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Shift-close cash variance threshold (JD)</label>
                  <input
                    type="number" min="0" step="0.001"
                    className={inputCls}
                    value={posShiftVarianceMaxJd}
                    onChange={e => setPosShiftVarianceMaxJd(e.target.value)}
                    disabled={!canManage}
                  />
                  <p className={hintCls}>
                    When closing a shift, if the cashier's counted cash differs from the
                    expected drawer cash by MORE than this amount (in either direction), the
                    till requires a typed reason and a manager PIN before the shift can close.
                    Default JD 5.000.
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Phone-in pay-by-link expiry (minutes)</label>
                  <input
                    type="number" min="5" max="240"
                    className={inputCls}
                    value={phoneInPayLinkExpiryMin}
                    onChange={e => setPhoneInPayLinkExpiryMin(e.target.value)}
                    disabled={!canManage}
                  />
                  <p className={hintCls}>
                    When a cashier rings a phone-in order with the <strong>Pay by SMS Link</strong>{' '}
                    payment method, the customer receives an SMS with a secure payment link.
                    If they don't complete payment within this window, the cron auto-cancels the
                    order (driver is not dispatched, stock is restored, loyalty + drops earn rows
                    are reversed). Default 30 min. Raise it for slower-paying customer bases,
                    lower it for tighter ops.
                  </p>
                </div>
              </div>

              {canManage && (
                <SaveRow
                  onSave={() => save(
                    {
                      pos_discount_no_pin_max_pct: posDiscountMaxPct,
                      // JD -> fils for storage
                      pos_discount_no_pin_max_fils: String(
                        Math.max(0, Math.round((parseFloat(posDiscountMaxJd) || 0) * 1000))
                      ),
                      pos_vat_exempt_pct: posVatExemptPct,
                      pos_vat_exempt_require_pin: posVatRequirePin ? 'true' : 'false',
                      pos_self_serve_void_window_minutes: posVoidWindowMinutes,
                      // JD -> fils for storage
                      pos_shift_variance_no_pin_max_fils: String(
                        Math.max(0, Math.round((parseFloat(posShiftVarianceMaxJd) || 0) * 1000))
                      ),
                      phone_in_pay_by_link_expiry_minutes: String(
                        Math.max(5, Math.min(240, parseInt(phoneInPayLinkExpiryMin, 10) || 30))
                      ),
                    },
                    setSavedPos, setErrorPos,
                  )}
                  isPending={patch.isPending}
                  saved={savedPos}
                  error={errorPos}
                />
              )}
            </div>
          )}

          {/* ── App Links ─────────────────────────────────────────────────── */}
          {activeTab === 'app' && (
            <div className="space-y-5">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3">
                <p className="text-xs text-amber-300 leading-relaxed">
                  Leave both URLs blank until the apps are live in the stores — the marketing
                  site (drinx.jo) hides every app reference (Get-the-app CTA, features section,
                  download buttons) automatically while both are empty. Fill them in when the
                  apps are published and the site reveals the app surfaces on the next
                  hourly cache refresh.
                </p>
              </div>
              <div>
                <label className={labelCls}>App Store URL (iOS)</label>
                <input
                  type="text"
                  value={appStoreUrl}
                  onChange={e => setAppStoreUrl(e.target.value)}
                  disabled={!canManage}
                  placeholder="e.g. https://apps.apple.com/jo/app/drinx/id1234567890"
                  className={inputCls}
                />
                <p className="text-xs text-gray-500 mt-1">Public link to the DrinX iOS app on the App Store.</p>
              </div>
              <div>
                <label className={labelCls}>App Store provider token (iOS campaigns)</label>
                <input
                  type="text"
                  value={appStoreProviderToken}
                  onChange={e => setAppStoreProviderToken(e.target.value)}
                  disabled={!canManage}
                  placeholder="From App Store Connect → App Analytics → Campaign Generator"
                  className={inputCls}
                />
                <p className="text-xs text-gray-500 mt-1">When set, /get-app appends Apple campaign params (pt/ct) to iOS links so installs attribute under App Store Connect → Sources → Campaigns. Blank = no campaign tracking.</p>
              </div>
              <div>
                <label className={labelCls}>Google Play URL (Android)</label>
                <input
                  type="text"
                  value={playStoreUrl}
                  onChange={e => setPlayStoreUrl(e.target.value)}
                  disabled={!canManage}
                  placeholder="e.g. https://play.google.com/store/apps/details?id=com.haddad.drinx"
                  className={inputCls}
                />
                <p className="text-xs text-gray-500 mt-1">Public link to the DrinX Android app on Google Play.</p>
              </div>
              {canManage && (
                <SaveRow
                  onSave={() => save(
                    { app_store_url: appStoreUrl.trim(), app_store_provider_token: appStoreProviderToken.trim(), play_store_url: playStoreUrl.trim() },
                    setSavedAppLinks, setErrorAppLinks,
                  )}
                  isPending={patch.isPending}
                  saved={savedAppLinks}
                  error={errorAppLinks}
                />
              )}

              <hr className="border-[#2e2e2e] my-4" />

              <h3 className="text-sm font-semibold text-white">DrinX Ambassador (driver) app</h3>
              <p className="text-xs text-gray-500 -mt-2">
                Public store links for the contracted-driver app. The /ambassadors recruitment page on
                drinx.jo switches its CTA from "Apply via email" to download buttons once both are set.
              </p>
              <div>
                <label className={labelCls}>Ambassador App Store URL (iOS)</label>
                <input
                  type="text"
                  value={ambAppStoreUrl}
                  onChange={e => setAmbAppStoreUrl(e.target.value)}
                  disabled={!canManage}
                  placeholder="e.g. https://apps.apple.com/jo/app/drinx-ambassador/id1234567890"
                  className={inputCls}
                />
                <p className="text-xs text-gray-500 mt-1">Public link to the DrinX Ambassador iOS app on the App Store.</p>
              </div>
              <div>
                <label className={labelCls}>Ambassador Google Play URL (Android)</label>
                <input
                  type="text"
                  value={ambPlayStoreUrl}
                  onChange={e => setAmbPlayStoreUrl(e.target.value)}
                  disabled={!canManage}
                  placeholder="e.g. https://play.google.com/store/apps/details?id=com.haddad.driverapp"
                  className={inputCls}
                />
                <p className="text-xs text-gray-500 mt-1">Public link to the DrinX Ambassador Android app on Google Play.</p>
              </div>
              {canManage && (
                <SaveRow
                  onSave={() => save(
                    {
                      ambassador_app_store_url: ambAppStoreUrl.trim(),
                      ambassador_play_store_url: ambPlayStoreUrl.trim(),
                    },
                    setSavedAmbLinks, setErrorAmbLinks,
                  )}
                  isPending={patch.isPending}
                  saved={savedAmbLinks}
                  error={errorAmbLinks}
                />
              )}
            </div>
          )}

          {/* ── WhatsApp message templates ─────────────────────────────────── */}
          {activeTab === 'whatsapp' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-white mb-1">WhatsApp message templates</h3>
                <p className="text-xs text-gray-500">
                  Turn each template on to send it over WhatsApp (falls back to SMS when off, or when the
                  customer has no WhatsApp). OTP codes are always sent over WhatsApp and aren't listed here.
                </p>
              </div>

              {waTemplatesQuery.data && !waTemplatesQuery.data.configured && (
                <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3">
                  <AlertTriangle size={16} />
                  WhatsApp isn't fully configured (token or WABA ID missing), so templates can't be listed.
                </div>
              )}

              {waTemplatesQuery.isLoading && (
                <p className="text-sm text-gray-500 py-4">Loading templates…</p>
              )}

              {waTemplatesQuery.data?.configured && waTemplatesQuery.data.templates.length === 0 && (
                <p className="text-sm text-gray-500 py-4">No approved templates found.</p>
              )}

              <div className="divide-y divide-white/5">
                {(waTemplatesQuery.data?.templates ?? []).map(t => (
                  <div key={`${t.name}|${t.language}`} className="flex items-center justify-between py-3">
                    <div className="min-w-0 pr-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white font-mono">{t.name}</span>
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/5 text-gray-400">{t.category || '—'}</span>
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-white/5 text-gray-500">{t.language}</span>
                        {t.buttonBroken && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">
                            <AlertTriangle size={11} /> button looks broken
                          </span>
                        )}
                      </div>
                      {t.buttonBroken && (
                        <p className="text-[11px] text-red-400/80 mt-1 font-mono break-all">{t.buttonUrl}</p>
                      )}
                    </div>
                    {canManage
                      ? <Toggle
                          enabled={t.enabled}
                          onChange={() => waToggle.mutate({ name: t.name, enabled: !t.enabled })}
                          disabled={waToggle.isPending}
                        />
                      : <span className={`text-xs font-semibold ${t.enabled ? 'text-green-400' : 'text-gray-500'}`}>{t.enabled ? 'On' : 'Off'}</span>
                    }
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Klaviyo (email marketing sync) ─────────────────────────────── */}
          {activeTab === 'klaviyo' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-white mb-1">Klaviyo email sync</h3>
                <p className="text-xs text-gray-500">
                  Push customers who gave an email to Klaviyo (with their tier, spend, last order, etc.)
                  and subscribe them for email campaigns. Re-run any time to refresh — it's idempotent.
                  Email only; SMS, WhatsApp and push stay on DrinX's own systems.
                </p>
              </div>

              {klaviyoStatus.isLoading && <p className="text-sm text-gray-500 py-2">Loading…</p>}

              {klaviyoStatus.data && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-sm text-gray-300">API key</span>
                    <span className={`text-xs font-semibold ${klaviyoStatus.data.api_key_set ? 'text-green-400' : 'text-red-400'}`}>
                      {klaviyoStatus.data.api_key_set ? 'Set' : 'Missing'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-sm text-gray-300">Target list</span>
                    <span className={`text-xs font-mono ${klaviyoStatus.data.list_id ? 'text-gray-300' : 'text-red-400'}`}>
                      {klaviyoStatus.data.list_id ?? 'Not set'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-sm text-gray-300">Eligible customers (have an email)</span>
                    <span className="text-sm text-white font-medium">{klaviyoStatus.data.eligible_customers}</span>
                  </div>
                </div>
              )}

              {user?.role === 'super_admin' ? (
                <button
                  onClick={() => klaviyoSync.mutate()}
                  disabled={klaviyoSync.isPending || !klaviyoStatus.data?.api_key_set || !klaviyoStatus.data?.list_id}
                  className="flex items-center gap-2 bg-[#c41e3a] hover:bg-[#a01830] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                >
                  <Mail size={15} />
                  {klaviyoSync.isPending ? 'Syncing…' : 'Sync to Klaviyo'}
                </button>
              ) : (
                <p className="text-xs text-gray-600">Only a super admin can run the sync.</p>
              )}

              {klaviyoSync.isSuccess && klaviyoSync.data && (
                <div className="text-green-400 text-sm bg-green-400/10 rounded-lg px-4 py-3">
                  Synced {klaviyoSync.data.synced} of {klaviyoSync.data.eligible} customers
                  {klaviyoSync.data.failed > 0 ? `, ${klaviyoSync.data.failed} failed (check logs)` : ''}.
                </div>
              )}
              {klaviyoSync.isError && (
                <div className="text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-3">
                  {(klaviyoSync.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Sync failed. Please try again.'}
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  )
}
